import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { customLocationApi } from '../../api/customLocationApi';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import SocialLocationSelector from '../social-compose/SocialLocationSelector';
import ExternalPlaceSearch from './ExternalPlaceSearch';

function locationId(value) {
  if (typeof value !== 'string' && !Number.isSafeInteger(value)) return null;
  return /^[1-9]\d*$/.test(String(value)) ? String(value) : null;
}

function usableOwnedLocations(rows) {
  if (!Array.isArray(rows)) throw new Error('invalid location list');
  return rows.filter((item) => ['PRIVATE', 'PUBLIC'].includes(item?.status)
    && typeof item.name === 'string' && item.name.trim() && locationId(item.linkedLocationId));
}

// 清單只存在於目前登入者的元件生命週期，不使用共用快取。
function OwnPrivateLocations({ onSelect, disabled }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [items, setItems] = useState([]);
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [publishNotice, setPublishNotice] = useState('');
  const [unconfirmed, setUnconfirmed] = useState(false);
  const publishing = useRef(false);
  const generation = useRef(0);
  useEffect(() => () => { generation.current += 1; }, []);

  async function load() {
    if (disabled || publishing.current) return;
    const request = ++generation.current;
    setOpen(true);
    setItems([]);
    setPublishTarget(null);
    setPublishError('');
    setPublishNotice('');
    setPhase('loading');
    try {
      const rows = usableOwnedLocations(await customLocationApi.listMine());
      if (request !== generation.current) return;
      setItems(rows);
      setUnconfirmed(false);
      setPhase('ready');
    } catch {
      if (request === generation.current) setPhase('error');
    }
  }

  function close() {
    if (publishing.current) return;
    generation.current += 1;
    setOpen(false);
    setItems([]);
    setPublishTarget(null);
  }

  async function publish() {
    const id = locationId(publishTarget?.id);
    if (!id || disabled || publishing.current || unconfirmed) return;
    const request = generation.current;
    const target = publishTarget;
    publishing.current = true;
    setPublishBusy(true);
    setPublishError('');
    setPublishNotice('');
    try {
      await customLocationApi.submitPublic(id);
      if (request !== generation.current) return;
      setItems((current) => current.map((item) => locationId(item.id) === id ? { ...item, status: 'PUBLIC' } : item));
      setPublishNotice(`已公開「${target.name}」。`);
    } catch {
      if (request !== generation.current) return;
      // 結果不明時先唯讀查證；不自動重送可能已成功的公開請求。
      try {
        const rows = usableOwnedLocations(await customLocationApi.listMine());
        if (request !== generation.current) return;
        const actual = rows.find((item) => locationId(item.id) === id);
        if (!actual) throw new Error('publication result unknown');
        setItems(rows);
        if (actual.status === 'PUBLIC') setPublishNotice(`已確認「${target.name}」已公開。`);
        else setPublishError('無法公開此地點，地點仍為私人狀態，請稍後再試。');
      } catch {
        if (request !== generation.current) return;
        setUnconfirmed(true);
        setPublishError('公開結果尚未確認，請重新載入地點清單後再操作；請勿重複送出。');
      }
    } finally {
      publishing.current = false;
      if (request === generation.current) {
        setPublishBusy(false);
        setPublishTarget(null);
      }
    }
  }

  return (
    <div className="journey-planning__custom-location" data-testid="own-private-location-picker">
      <button type="button" aria-expanded={open} disabled={disabled || publishBusy} onClick={open ? close : load}>
        {open ? '收起私人地點' : '使用既有私人地點'}
      </button>
      {open && (
        <div role="group" aria-label="我的私人地點">
          {phase === 'loading' && <p role="status">載入私人地點中…</p>}
          {phase === 'error' && <>
            <p role="alert">無法載入私人地點，請稍後重試。</p>
            <button type="button" disabled={disabled} onClick={load}>重試私人地點</button>
          </>}
          {publishError && <p role="alert">{publishError}</p>}
          {publishNotice && <p role="status">{publishNotice}</p>}
          {unconfirmed && <button type="button" disabled={disabled || publishBusy} onClick={load}>重新載入地點清單</button>}
          {phase === 'ready' && (items.length ? <ul>
            {items.map((item) => <li key={String(item.linkedLocationId)}>
              <p>{item.name}：{item.status === 'PUBLIC' ? '已公開（PUBLIC）' : '僅自己（PRIVATE）'}</p>
              <button type="button" disabled={disabled || publishBusy} onClick={() => {
                if (disabled || publishing.current) return;
                onSelect({
                  locationId: String(item.linkedLocationId), displayName: item.name.trim(),
                  canonicalName: item.name.trim(), locationType: 'POI', hasChildren: false,
                  latitude: item.latitude ?? null, longitude: item.longitude ?? null,
                  address: item.addressText ?? null,
                });
                close();
              }}>{item.status === 'PRIVATE' ? '選用私人地點：' : '選用已公開地點：'}{item.name}</button>
              {item.status === 'PRIVATE' && <button type="button"
                disabled={disabled || publishBusy || unconfirmed || !locationId(item.id)}
                onClick={() => setPublishTarget(item)}>公開此地點：{item.name}</button>}
            </li>)}
          </ul> : <p>目前沒有可用的私人地點，可使用下方「新增自訂地點」。</p>)}
        </div>
      )}
      <ConfirmDialog open={Boolean(publishTarget)} title="公開既有地點？"
        description={`即將公開「${publishTarget?.name || ''}」。其他使用者可能看見地點名稱、座標及地址，請確認不含私人地址或個人資訊。這不會自動公開行程；取消活動編輯也不會撤銷地點公開。`}
        confirmLabel="確認公開地點" cancelLabel="取消公開" loading={publishBusy}
        onConfirm={publish} onCancel={() => { if (!publishing.current) setPublishTarget(null); }} />
    </div>
  );
}

/**
 * Activity 地點選擇器（MAP-GOOGLE-E1）。
 * 組合 SocialLocationSelector（階層瀏覽）與 ExternalPlaceSearch（外部搜尋）。
 *
 * onSelect({ locationId, displayName }) — 統一介面，Activity write 只使用 locationId。
 * searchProvider 為 createExternalLocationSearchProvider() 回傳值；
 * disabled 時為 mode='disabled'，不顯示外部搜尋。
 *
 * 注意：onSelect 回傳的 node shape 與 SocialLocationSelector 相容（含 locationId/displayName），
 * 確保 Activity write path 只攜帶 locationId，不含 providerPlaceId。
 */
export default function ActivityLocationPicker({
  selected,
  onSelect,
  searchProvider,
  disabled = false,
}) {
  const { user, isAuthenticated } = useAuth();
  const memberKey = isAuthenticated ? String(user?.memberId ?? user?.id ?? '') : '';

  function handleExternalResolved(resolved) {
    // 只取 locationId + displayName，不保留 providerPlaceId 到 Activity 欄位
    onSelect({
      locationId: resolved.locationId,
      displayName: resolved.displayName,
      // UX-03-B: draft-only coordinates for unsaved map preview.
      // API payload remains unchanged (submit uses only locationId).
      latitude: resolved.latitude ?? null,
      longitude: resolved.longitude ?? null,
      address: resolved.address ?? null,
    });
  }

  return (
    <div className="activity-location-picker">
      {memberKey && <OwnPrivateLocations key={memberKey} onSelect={onSelect} disabled={disabled} />}
      <SocialLocationSelector
        selected={selected}
        onSelect={onSelect}
        disabled={disabled}
      />
      {searchProvider?.enabled && (
        <ExternalPlaceSearch
          searchProvider={searchProvider}
          onResolved={handleExternalResolved}
          disabled={disabled}
        />
      )}
    </div>
  );
}
